import { useEffect, useState } from "react";
import { FiCalendar, FiDownload, FiExternalLink, FiMessageSquare, FiShare2, FiUserCheck } from "react-icons/fi";
import RequestDocumentSignOffModal from "@/components/patient/documents/request-document-signoff-modal";
import SendDocumentTextDialog from "@/components/patient/documents/send-document-text-dialog";
import SignedOff from "@/components/signed-off";
import { Tooltip } from "@/components/ui";
import Button from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Divider from "@/components/ui/divider";
import { SmartDateInput } from "@/components/ui/smart-date-input";
import Textarea from "@/components/ui/textarea";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useIsMobileOrTablet } from "@/hooks/use-is-touch-device";
import { pickedDay } from "@/lib/clinic-clock";
import { formatDate, fromDateOnlyString } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { toast } from "@/lib/use-toast";
import { DocumentSummary, ID_DOCUMENT_TYPE_NAME } from "@/types/document";
import ExpiryBadge from "./expiry-badge";
import { downloadFromSignedUrl, getSignedDownloadUrl, getSignedUrl } from "@/api/auth/gcs";
import { useSetDocumentExpiryMutation, useShareDocumentMutation, useSignOffDocumentMutation, useUpdateDocumentReviewCommentMutation } from "@/api/provider/user/patient/document-mutation";
interface DocumentViewDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  document: DocumentSummary | null;
  /** Called with the updated document after a sign-off / review-comment write
   * so the caller can refresh the snapshot it passes back in as `document`. */
  onDocumentUpdated?: (document: DocumentSummary) => void;
}
export default function DocumentViewDialog({
  isOpen,
  onOpenChange,
  document,
  onDocumentUpdated
}: DocumentViewDialogProps) {
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  // The whole review/sign-off surface is opt-in per clinic (default off).
  const signOffEnabled = tenant?.documentSignOffEnabled === true;
  const canDownload = hasPermission(currentUser, PERMISSIONS.DOWNLOADS_EXPORT);
  // Changing a document (rename/retype/expiry/delete) needs documents-manage;
  // documents-read only views and uploads. Matches RequirePatientsDocumentsManage.
  const canEditDocuments = hasPermission(currentUser, PERMISSIONS.PATIENTS_DOCUMENTS_MANAGE);
  const canSignOff = signOffEnabled && hasPermission(currentUser, PERMISSIONS.SIGN_OFF_DOCUMENTS);
  // Reviewing and commenting is its own authority: front desk staff confirm a consent
  // form was completed without being able to sign off anything clinical. Sign-off
  // implies it (see apply_permission_implications), so signers keep the note box.
  const canReview = signOffEnabled && hasPermission(currentUser, PERMISSIONS.PATIENTS_DOCUMENTS_REVIEW);
  const canRequestSignOff = signOffEnabled && hasPermission(currentUser, PERMISSIONS.TASKS_WRITE);
  const isMobileOrTablet = useIsMobileOrTablet();
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  // Set when the browser cannot decode the file behind a perfectly good signed URL, so the
  // preview can say so instead of leaving a broken image icon with no way forward.
  const [previewFailed, setPreviewFailed] = useState(false);
  const [comment, setComment] = useState("");
  const [requestSignOffOpen, setRequestSignOffOpen] = useState(false);
  const [sendTextOpen, setSendTextOpen] = useState(false);
  const [isEditingExpiry, setIsEditingExpiry] = useState(false);
  const [expiryDraft, setExpiryDraft] = useState<Date | undefined>(undefined);
  const signOffMutation = useSignOffDocumentMutation();
  const updateCommentMutation = useUpdateDocumentReviewCommentMutation();
  const shareMutation = useShareDocumentMutation();
  const setExpiryMutation = useSetDocumentExpiryMutation();

  // Mirror the document locally so an edit (expiry, sign-off, share) reflects
  // immediately, even when the parent holds the selected document in its own
  // state (e.g. the Documents tab) and doesn't replace it after the mutation.
  const [displayDocument, setDisplayDocument] = useState(document);
  useEffect(() => {
    setDisplayDocument(document);
  }, [document]);
  const handleDocumentUpdated = (updated: DocumentSummary) => {
    setDisplayDocument(updated);
    onDocumentUpdated?.(updated);
  };
  const isReviewed = Boolean(displayDocument?.reviewedDate);
  // Keep the local note box in sync with whichever document is open.
  useEffect(() => {
    setComment(displayDocument?.reviewComment ?? "");
  }, [displayDocument?.id, displayDocument?.reviewComment]);

  // Reset the expiry editor whenever the shown document changes. Parse the
  // date-only value as a calendar date so the picker shows the right day
  // regardless of timezone.
  useEffect(() => {
    setIsEditingExpiry(false);
    setExpiryDraft(displayDocument?.expiryDate ? fromDateOnlyString(displayDocument.expiryDate.slice(0, 10)) : undefined);
  }, [displayDocument?.id, displayDocument?.expiryDate]);

  // Show the expiry row for ID documents, or any document that already has one.
  // Exact match (no trim/case-fold) stays consistent with the tenant gate and
  // the doc_type=ID fetch, so we don't offer to set an expiry on a near-miss
  // type ("Id"/"ID ") that would never surface in the Identification card.
  const showExpiry = !!displayDocument && (displayDocument.docType === ID_DOCUMENT_TYPE_NAME || !!displayDocument.expiryDate);
  const handleSaveExpiry = (nextExpiry: string | null) => {
    if (!displayDocument) return;
    setExpiryMutation.mutate({
      documentId: displayDocument.id,
      patientId: displayDocument.patientId,
      expiryDate: nextExpiry
    }, {
      onSuccess: updated_0 => {
        setIsEditingExpiry(false);
        if (updated_0) setDisplayDocument(updated_0);
      }
    });
  };

  // Sign the viewer URL when the dialog opens (not on mount): the dialog is
  // often mounted while closed, and a GCS signed URL only lives ~15m, so signing
  // eagerly can hand back a stale URL by the time the user actually opens it.
  useEffect(() => {
    setPreviewFailed(false);
    if (!isOpen || !document) {
      setViewerUrl(null);
      return;
    }
    let cancelled = false;
    const loadDocument = async () => {
      try {
        const response = await getSignedUrl(document.filePath);
        if (!cancelled && response) {
          setViewerUrl(response.signedUrl);
        }
      } catch {
        if (!cancelled) {
          toast({
            title: "Error",
            description: "Failed to load document",
            variant: "error"
          });
        }
      }
    };
    loadDocument();
    return () => {
      cancelled = true;
    };
  }, [isOpen, document]);

  // "Driver's License (Front)" + the stored extension, so the saved file opens in whatever the
  // desktop associates with it rather than landing as an extensionless blob.
  const downloadFilename = (() => {
    const storedExtension = document?.filePath?.split(".").pop();
    const base = document?.title?.trim() || "document";
    return storedExtension && storedExtension !== document?.filePath ? `${base}.${storedExtension}` : base;
  })();
  const handleDownload = async () => {
    if (!document) return;
    try {
      // Ask for an attachment-disposition URL and click it, rather than opening the inline one in
      // a tab: the inline URL carries the file's own content type, so a format the browser cannot
      // render (an iPhone HEIC stored before uploads were normalised) opened a blank tab and never
      // saved anything — the download looked like it did nothing at all.
      const response_0 = await getSignedDownloadUrl(document.filePath, downloadFilename);
      if (response_0?.signedUrl) {
        await downloadFromSignedUrl(response_0.signedUrl, downloadFilename);
      } else {
        toast({
          title: "Error",
          description: "Failed to download document",
          variant: "error"
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to download document",
        variant: "error"
      });
    }
  };
  const handleSignOff = () => {
    if (!displayDocument) return;
    signOffMutation.mutate({
      documentId: displayDocument.id,
      patientId: displayDocument.patientId,
      // Send exactly what's in the box (the box is pre-filled with any
      // existing note), so signing off persists what the reviewer sees —
      // including clearing the note if they emptied it.
      comment: comment.trim()
    }, {
      onSuccess: updated_1 => {
        if (updated_1) handleDocumentUpdated(updated_1);
      }
    });
  };
  const handleSaveNote = () => {
    if (!displayDocument) return;
    updateCommentMutation.mutate({
      documentId: displayDocument.id,
      patientId: displayDocument.patientId,
      comment: comment.trim()
    }, {
      onSuccess: updated_2 => {
        if (updated_2) handleDocumentUpdated(updated_2);
      }
    });
  };
  const handleToggleShare = () => {
    if (!displayDocument) return;
    shareMutation.mutate({
      documentId: displayDocument.id,
      patientId: displayDocument.patientId,
      shared: !displayDocument.sharedWithPatient
    }, {
      onSuccess: updated_3 => {
        if (updated_3) handleDocumentUpdated(updated_3);
      }
    });
  };
  const noteChanged = (displayDocument?.reviewComment ?? "") !== comment.trim();
  return <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="truncate">{displayDocument?.title}</span>
              {signOffEnabled && isReviewed && <SignedOff signOff={{
              signedOffProviderId: displayDocument?.reviewedByProviderId ?? undefined,
              signedOffDate: displayDocument?.reviewedDate ? new Date(displayDocument.reviewedDate) : undefined
            }} />}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            {showExpiry && <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-background-secondary px-3 py-2">
                <div className="flex items-center gap-2 text-body text-foreground">
                  <FiCalendar className="h-4 w-4 text-foreground-secondary" />
                  <span className="text-foreground-secondary">Expires:</span>
                  <span className="font-medium">
                    {displayDocument?.expiryDate ? formatDate(displayDocument.expiryDate) : "Not set"}
                  </span>
                  <ExpiryBadge expiryDate={displayDocument?.expiryDate} />
                </div>
                {canEditDocuments && !isEditingExpiry && <Button variant="ghost" size="sm" className="ml-auto text-primary" onClick={() => setIsEditingExpiry(true)}>
                    {displayDocument?.expiryDate ? "Edit" : "Set expiry"}
                  </Button>}
                {canEditDocuments && isEditingExpiry && <div className="ml-auto flex flex-wrap items-center gap-2">
                    <SmartDateInput value={expiryDraft} onChange={setExpiryDraft} direction="future" placeholder="Select date" />
                    <Button variant="primary" size="sm" disabled={!expiryDraft || setExpiryMutation.isPending} onClick={() => expiryDraft && handleSaveExpiry(pickedDay(expiryDraft))}>
                      Save
                    </Button>
                    {displayDocument?.expiryDate && <Button variant="ghost" size="sm" disabled={setExpiryMutation.isPending} onClick={() => handleSaveExpiry(null)}>
                        Clear
                      </Button>}
                    <Button variant="secondary" size="sm" disabled={setExpiryMutation.isPending} onClick={() => {
                setIsEditingExpiry(false);
                setExpiryDraft(displayDocument?.expiryDate ? fromDateOnlyString(displayDocument.expiryDate.slice(0, 10)) : undefined);
              }}>
                      Cancel
                    </Button>
                  </div>}
              </div>}
            <div>
              {viewerUrl && (document?.fileType?.toLowerCase().includes("pdf") ? isMobileOrTablet ? <div className="flex h-[200px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed text-center">
                      <p className="text-body text-foreground-secondary">
                        PDF preview is not supported on this device
                      </p>
                      <Button onClick={() => window.open(viewerUrl, "_blank")} variant="primary">
                        <FiExternalLink className="mr-2 h-4 w-4" />
                        Open PDF
                      </Button>
                    </div> : <iframe src={viewerUrl} className={`${signOffEnabled ? "h-[55dvh]" : "h-[78dvh]"} w-full rounded-lg border`} title={document?.title} /> : document?.fileType?.toLowerCase().includes("image") ? previewFailed ? <div className="flex h-[200px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed text-center">
                      <p className="text-body text-foreground-secondary">
                        This image can&apos;t be shown in your browser
                      </p>
                      <Button onClick={handleDownload} variant="primary" disabled={!canDownload}>
                        <FiDownload className="mr-2 h-4 w-4" />
                        Download to view
                      </Button>
                    </div> : <img src={viewerUrl} alt={document?.title} onError={() => setPreviewFailed(true)} className={`${signOffEnabled ? "max-h-[55dvh]" : "max-h-[78dvh]"} w-full rounded-lg border object-contain`} /> : <div className="flex h-[200px] flex-col items-center justify-center rounded-lg border border-dashed text-center">
                    <p className="text-body text-muted-foreground">
                      Preview not available for this file type
                    </p>
                    <p className="mt-1 text-caption text-foreground-muted">
                      Use the Download button to view this file
                    </p>
                  </div>)}
            </div>

            <div className="space-y-4">
              {displayDocument?.summary && <div className="space-y-2">
                  <h3 className="text-body-strong text-muted-foreground">
                    Summary
                  </h3>
                  <p className="text-body text-foreground">
                    {displayDocument.summary}
                  </p>
                  <Divider />
                </div>}

              {signOffEnabled && <div className="space-y-2">
                  <h3 className="text-body-strong text-foreground">Review</h3>
                  {isReviewed && <p className="text-caption text-foreground-secondary">
                      This document has been reviewed and signed off.
                    </p>}
                  <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Add an internal note or addendum about this document…" rows={3} disabled={!canReview} />
                  {canReview || canRequestSignOff ? <div className="flex flex-wrap gap-2">
                      {canReview && <Button variant="primary" size="sm" onClick={handleSaveNote} disabled={!noteChanged} isLoading={updateCommentMutation.isPending}>
                          Save note
                        </Button>}
                      {canSignOff && !isReviewed && <Button variant="secondary" size="sm" onClick={handleSignOff} isLoading={signOffMutation.isPending}>
                          Sign off
                        </Button>}
                      {canRequestSignOff && !isReviewed && <Button variant="secondary" size="sm" icon={<FiUserCheck className="h-4 w-4" />} onClick={() => setRequestSignOffOpen(true)}>
                          Request sign-off
                        </Button>}
                      {canSignOff && <Button variant="secondary" size="sm" icon={<FiShare2 className="h-4 w-4" />} onClick={handleToggleShare} isLoading={shareMutation.isPending}>
                          {displayDocument?.sharedWithPatient ? "Remove from portal" : "Share to portal"}
                        </Button>}
                      {canSignOff && <Button variant="secondary" size="sm" icon={<FiMessageSquare className="h-4 w-4" />} onClick={() => setSendTextOpen(true)}>
                          Send via text
                        </Button>}
                    </div> : <p className="text-caption text-foreground-muted">
                      You don&apos;t have permission to review documents.
                    </p>}
                  {displayDocument?.sharedWithPatient && <p className="text-caption text-status-success-text">
                      Visible in the patient&apos;s portal.
                    </p>}
                </div>}
            </div>
          </DialogBody>
          <DialogFooter>
            <Tooltip content={!canDownload ? "You don't have permission to download files" : undefined} triggerAsChild>
              <div tabIndex={canDownload ? undefined : 0}>
                <Button variant="secondary" onClick={handleDownload} className="mr-2" disabled={!canDownload}>
                  <FiDownload className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </div>
            </Tooltip>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {displayDocument && <RequestDocumentSignOffModal document={displayDocument} open={requestSignOffOpen} onOpenChange={setRequestSignOffOpen} />}
      {displayDocument && <SendDocumentTextDialog document={displayDocument} open={sendTextOpen} onOpenChange={setSendTextOpen} onSent={handleDocumentUpdated} />}
    </>;
}